/**
 * Top Fonds Handler - Plain JavaScript version
 * Converted from jQuery-based accordion and tab navigation
 */

class TopFondsHandler {
  constructor() {
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    document.addEventListener('DOMContentLoaded', () => {
      this.attachEventListeners();
      this.initializeAccordionPanels();
    });
  }

  attachEventListeners() {
    // Tab header navigation (only mobile/small tablet < 900px)
    document.addEventListener('click', (event) => {
      const tabnavHeader = event.target.closest('.topfonds-header-tabs .tabnav-header-background');
      if (tabnavHeader) {
        this.handleTabHeaderClick(event, tabnavHeader);
      }
    });

    document.addEventListener('touchstart', (event) => {
      const tabnavHeader = event.target.closest('.topfonds-header-tabs .tabnav-header-background');
      if (tabnavHeader) {
        this.handleTabHeaderClick(event, tabnavHeader);
      }
    });

    // Tab navigation item click
    document.addEventListener('click', (event) => {
      const toggleElement = event.target.closest('.opened.topfonds-header-tabs .tabnav-item [data-toggle]');
      if (toggleElement) {
        this.handleTabItemClick(event, toggleElement);
      }
    });

    // Accordion header click
    document.addEventListener('click', (event) => {
      const accordionHeader = event.target.closest('.topfonds-item .topfonds-item-header');
      if (accordionHeader) {
        this.handleAccordionHeaderClick(event, accordionHeader);
      }
    });

    // Accordion header keyboard navigation
    document.addEventListener('keydown', (event) => {
      const accordionHeader = event.target.closest('.topfonds-item .topfonds-item-header');
      if (accordionHeader && (event.key === 'Enter' || event.key === ' ' || event.keyCode === 13 || event.keyCode === 32)) {
        event.preventDefault();
        event.stopPropagation();
        this.handleAccordionHeaderKeydown(event, accordionHeader);
      }
    });

    // More/Less toggle
    document.addEventListener('click', (event) => {
      const moreButton = event.target.closest('.topfonds-more .more');
      if (moreButton) {
        this.handleMoreToggle(event, moreButton);
      }
    });
  }

  /**
   * Handle tab header click (mobile/small tablet only)
   */
  handleTabHeaderClick(event, tabnavHeader) {
    if (document.documentElement.clientWidth >= 900) return;

    event.preventDefault();

    const tabsContainer = tabnavHeader.closest('.topfonds-header-tabs');
    if (tabsContainer) {
      const isOpened = tabsContainer.classList.contains('opened');
      tabsContainer.classList.toggle('opened', !isOpened);

      // Handle mouseout event for closing
      if (event.type === 'mouseover') {
        const handleMouseout = () => {
          tabsContainer.classList.remove('opened');
          tabsContainer.removeEventListener('mouseout', handleMouseout);
        };
        tabsContainer.addEventListener('mouseout', handleMouseout);
      }
    }
  }

  /**
   * Handle tab navigation item click
   */
  handleTabItemClick(event, toggleElement) {
    event.preventDefault();
    const target = toggleElement.getAttribute('data-toggle');
    const isTab = toggleElement.classList.contains('js_tab');

    // Assuming amApp.togglePane exists and works similar to the original
    if (typeof window.amApp !== 'undefined' && window.amApp.togglePane) {
      window.amApp.togglePane(target, isTab);
    }

    const tabsContainer = toggleElement.closest('.topfonds-header-tabs');
    if (tabsContainer) {
      // Update active tab text
      const activeTabElement = tabsContainer.querySelector('.activeTab');
      if (activeTabElement) {
        activeTabElement.textContent = toggleElement.textContent;
      }

      // Close the tabs
      tabsContainer.classList.remove('opened');
    }
  }

  /**
   * Handle accordion header click
   */
  handleAccordionHeaderClick(event, header) {
    const item = header.closest('.topfonds-item');
    if (!item) return;

    const panel = item.querySelector('.topfonds-item-pane');
    if (!panel) return;

    // Check if item is currently opened
    const isCurrentlyOpened = item.classList.contains('opened');

    // Toggle opened status for clicked item
    item.classList.toggle('opened');
    const isNowOpened = item.classList.contains('opened');
    header.setAttribute('aria-expanded', isNowOpened);
    this.manageAccordionPanelFocus(panel, isNowOpened);

    // Handle sibling items - close all others
    const siblings = item.parentNode.querySelectorAll('.topfonds-item.opened');
    siblings.forEach(sibling => {
      if (sibling !== item) {
        sibling.classList.remove('opened');
        const siblingHeader = sibling.querySelector('.topfonds-item-header');
        if (siblingHeader) {
          siblingHeader.setAttribute('aria-expanded', 'false');
        }
        const siblingPanel = sibling.querySelector('.topfonds-item-pane');
        if (siblingPanel) {
          this.manageAccordionPanelFocus(siblingPanel, false);
        }
      }
    });
  }

  /**
   * Handle accordion header keyboard interaction
   */
  handleAccordionHeaderKeydown(event, header) {
    const item = header.closest('.topfonds-item');
    if (!item) return;

    const panel = item.querySelector('.topfonds-item-pane');
    if (!panel) return;

    // Check if item is currently opened
    const isCurrentlyOpened = item.classList.contains('opened');

    // Toggle opened status for clicked item
    item.classList.toggle('opened');
    const isNowOpened = item.classList.contains('opened');
    header.setAttribute('aria-expanded', isNowOpened);
    this.manageAccordionPanelFocus(panel, isNowOpened);

    // Handle sibling items - close all others
    const siblings = item.parentNode.querySelectorAll('.topfonds-item.opened');
    siblings.forEach(sibling => {
      if (sibling !== item) {
        sibling.classList.remove('opened');
        const siblingHeader = sibling.querySelector('.topfonds-item-header');
        if (siblingHeader) {
          siblingHeader.setAttribute('aria-expanded', 'false');
        }
        const siblingPanel = sibling.querySelector('.topfonds-item-pane');
        if (siblingPanel) {
          this.manageAccordionPanelFocus(siblingPanel, false);
        }
      }
    });
  }

  /**
   * Handle more/less toggle
   */
  handleMoreToggle(event, moreButton) {
    // Toggle d-none classes between more buttons
    const container = moreButton.closest('.topfonds-more');
    if (container) {
      const moreButtons = container.querySelectorAll('.more');
      moreButtons.forEach(btn => btn.classList.toggle('d-none'));

      // Hide/remove opened status from all sibling items and toggle hidden class
      const fondsList = container.parentNode.querySelector('.topfonds-list');
      if (fondsList) {
        const page2Items = fondsList.querySelectorAll('.topfonds-page2');
        page2Items.forEach(item => {
          item.classList.remove('opened');
          item.classList.toggle('hidden');
        });
      }
    }
  }

  /**
   * Manage focusability of elements inside accordion panel
   */
  manageAccordionPanelFocus(panel, makeFocusable) {
    const focusableElements = panel.querySelectorAll(
      'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (makeFocusable) {
      focusableElements.forEach(element => {
        element.removeAttribute('tabindex');
      });
      // If the panel itself was made non-focusable, restore its focusability if needed
      // panel.removeAttribute('tabindex');
    } else {
      focusableElements.forEach(element => {
        element.setAttribute('tabindex', '-1');
      });
      // If the panel itself needs to be non-focusable
      // panel.setAttribute('tabindex', '-1');
    }
  }

  /**
   * Initialize accordion panels on page load
   */
  initializeAccordionPanels() {
    const items = document.querySelectorAll('.topfonds-item');
    items.forEach(item => {
      const panel = item.querySelector('.topfonds-item-pane');
      const header = item.querySelector('.topfonds-item-header');

      if (!item.classList.contains('opened')) {
        // If the panel is initially closed
        if (panel) {
          this.manageAccordionPanelFocus(panel, false);
        }
        // Ensure aria-expanded is correctly set for initially closed items
        if (header) {
          header.setAttribute('aria-expanded', 'false');
        }
      } else {
        // If the panel is initially opened
        if (panel) {
          this.manageAccordionPanelFocus(panel, true);
        }
        // Ensure aria-expanded is correctly set for initially opened items
        if (header) {
          header.setAttribute('aria-expanded', 'true');
        }
      }
    });
  }
}

// Initialize the handler
const topFondsHandler = new TopFondsHandler();

// Export for potential module usage
export default TopFondsHandler;